import type { JsonValue } from '../../serializable';
import {
  WorkflowNodeType,
  type DraftAdHocAgentNodeParams,
  type DraftAgentNodeParams,
  type DraftCompleteToolCallNodeParams,
  type DraftFilterNodeParams,
  type DraftForEachNodeParams,
  type DraftRunWorkflowNodeParams,
  type DraftEmitEventNodeParams,
  type DraftMapperNodeParams,
  type DraftDebounceNodeParams,
  type DraftBucketNodeParams,
  type DraftGroupByNodeParams,
  type DraftToolCallNodeParams,
} from '../types';
import {
  WorkflowGraphNode,
  type WorkflowNodeConnectOptions,
} from './workflow-graph-node';

export class ToolCallNode extends WorkflowGraphNode {
  readonly nodeType = WorkflowNodeType.ToolCall;

  constructor(
    private readonly params: Omit<
      DraftToolCallNodeParams,
      'id' | 'type' | 'data'
    > & {
      readonly data?: Record<string, unknown>;
    },
    options?: WorkflowNodeConnectOptions
  ) {
    super(options);
  }

  override toSerializedNode(serialization: {
    workflowId: string;
    ownerId: string;
    ownerOrgId: string;
    createdAt: string;
    updatedAt: string;
  }) {
    return this.baseSerializedNode({
      ...serialization,
      data: {
        ...(this.params.data ?? {}),
        key: this.params.key,
        params: this.params.params as JsonValue,
        ...(this.params.paramBindings !== undefined && {
          paramBindings: this.params.paramBindings,
        }),
        ...(this.params.interruptableTool !== undefined && {
          interruptableTool: this.params.interruptableTool,
        }),
      },
      inputShape: this.params.inputShape ?? null,
      defaultOutput: (this.params.defaultOutput ?? null) as JsonValue | null,
    });
  }
}

export class AgentNode extends WorkflowGraphNode {
  readonly nodeType = WorkflowNodeType.Agent;

  constructor(
    private readonly params: Omit<DraftAgentNodeParams, 'id' | 'type'>,
    options?: WorkflowNodeConnectOptions
  ) {
    super(options);
  }

  override toSerializedNode(serialization: {
    workflowId: string;
    ownerId: string;
    ownerOrgId: string;
    createdAt: string;
    updatedAt: string;
  }) {
    return this.baseSerializedNode({
      ...serialization,
      data: {
        ...(this.params.data ?? {}),
        agentId: this.params.agentId,
      },
      inputShape: this.params.inputShape ?? null,
      defaultOutput: (this.params.defaultOutput ?? null) as JsonValue | null,
    });
  }
}

export class AdHocAgentNode extends WorkflowGraphNode {
  readonly nodeType = WorkflowNodeType.AdHocAgent;

  constructor(
    private readonly params: Omit<DraftAdHocAgentNodeParams, 'id' | 'type'>,
    options?: WorkflowNodeConnectOptions
  ) {
    super(options);
  }

  override toSerializedNode(serialization: {
    workflowId: string;
    ownerId: string;
    ownerOrgId: string;
    createdAt: string;
    updatedAt: string;
  }) {
    return this.baseSerializedNode({
      ...serialization,
      data: {
        ...(this.params.data ?? {}),
        model: this.params.model,
        ...(this.params.system !== undefined && { system: this.params.system }),
        ...(this.params.toolKeys !== undefined && {
          toolKeys: this.params.toolKeys,
        }),
        ...(this.params.mode !== undefined && { mode: this.params.mode }),
        ...(this.params.agent !== undefined && { agent: this.params.agent }),
        ...(this.params.agentId !== undefined && {
          agentId: this.params.agentId,
        }),
        ...(this.params.outputShape !== undefined && {
          outputShape: this.params.outputShape,
        }),
        ...(this.params.conversationId !== undefined && {
          conversationId: this.params.conversationId,
        }),
        ...(this.params.parentConversationId !== undefined && {
          parentConversationId: this.params.parentConversationId,
        }),
        ...(this.params.parentToolCallId !== undefined && {
          parentToolCallId: this.params.parentToolCallId,
        }),
      },
      inputShape: this.params.inputShape ?? null,
      defaultOutput: (this.params.defaultOutput ?? null) as JsonValue | null,
    });
  }
}

export class CompleteToolCallNode extends WorkflowGraphNode {
  readonly nodeType = WorkflowNodeType.CompleteToolCall;

  constructor(
    private readonly params: Omit<
      DraftCompleteToolCallNodeParams,
      'id' | 'type'
    >,
    options?: WorkflowNodeConnectOptions
  ) {
    super(options);
  }

  override toSerializedNode(serialization: {
    workflowId: string;
    ownerId: string;
    ownerOrgId: string;
    createdAt: string;
    updatedAt: string;
  }) {
    return this.baseSerializedNode({
      ...serialization,
      data: {
        ...(this.params.data ?? {}),
        parentConversationId: this.params.parentConversationId,
        toolCallId: this.params.toolCallId,
        parentModel: this.params.parentModel,
        ...(this.params.mode !== undefined && { mode: this.params.mode }),
      },
      inputShape: this.params.inputShape ?? null,
      defaultOutput: (this.params.defaultOutput ?? null) as JsonValue | null,
    });
  }
}

/** Serializes one filter condition, omitting absent optional fields. */
function serializeFilterCondition(condition: {
  path?: string;
  operator?: string;
  value?: string;
}): Record<string, JsonValue> {
  const entry: Record<string, JsonValue> = {};
  if (condition.path !== undefined) {
    entry['path'] = condition.path;
  }
  if (condition.operator !== undefined) {
    entry['operator'] = condition.operator;
  }
  if (condition.value !== undefined) {
    entry['value'] = condition.value;
  }
  return entry;
}

export class FilterNode extends WorkflowGraphNode {
  readonly nodeType = WorkflowNodeType.Filter;

  constructor(
    private readonly params: Omit<DraftFilterNodeParams, 'id' | 'type'>,
    options?: WorkflowNodeConnectOptions
  ) {
    super(options);
  }

  override toSerializedNode(serialization: {
    workflowId: string;
    ownerId: string;
    ownerOrgId: string;
    createdAt: string;
    updatedAt: string;
  }) {
    const data: Record<string, JsonValue> = {};
    // Current shape: groups combined by the top combinator, each group combining
    // its conditions by its own combinator. The `conditions` and top-level
    // path/operator/value shapes are still serialized for older nodes.
    if (this.params.combinator !== undefined) {
      data['combinator'] = this.params.combinator;
    }
    if (this.params.groups !== undefined) {
      data['groups'] = this.params.groups.map((group) => {
        const groupData: Record<string, JsonValue> = {
          conditions: (group.conditions ?? []).map(serializeFilterCondition),
        };
        if (group.combinator !== undefined) {
          groupData['combinator'] = group.combinator;
        }
        return groupData;
      });
    }
    if (this.params.conditions !== undefined) {
      data['conditions'] = this.params.conditions.map(serializeFilterCondition);
    }
    if (this.params.path !== undefined) {
      data['path'] = this.params.path;
    }
    if (this.params.operator !== undefined) {
      data['operator'] = this.params.operator;
    }
    if (this.params.value !== undefined) {
      data['value'] = this.params.value;
    }
    return this.baseSerializedNode({
      ...serialization,
      data,
      inputShape: this.params.inputShape ?? null,
      defaultOutput: this.params.defaultOutput ?? null,
    });
  }
}

export class RunWorkflowNode extends WorkflowGraphNode {
  readonly nodeType = WorkflowNodeType.RunWorkflow;

  constructor(
    private readonly params: Omit<DraftRunWorkflowNodeParams, 'id' | 'type'>,
    options?: WorkflowNodeConnectOptions
  ) {
    super(options);
  }

  override toSerializedNode(serialization: {
    workflowId: string;
    ownerId: string;
    ownerOrgId: string;
    createdAt: string;
    updatedAt: string;
  }) {
    const data: Record<string, unknown> = {
      targetWorkflowId: this.params.targetWorkflowId,
    };
    if (this.params.targetRunId !== undefined) {
      data['targetRunId'] = this.params.targetRunId;
    }
    if (this.params.name !== undefined) {
      data['name'] = this.params.name;
    }
    if (this.params.inputBindings !== undefined) {
      data['inputBindings'] = { ...this.params.inputBindings };
    }
    return this.baseSerializedNode({
      ...serialization,
      data,
      inputShape: this.params.inputShape ?? null,
      defaultOutput: (this.params.defaultOutput ?? null) as JsonValue | null,
    });
  }
}

export class EmitEventNode extends WorkflowGraphNode {
  readonly nodeType = WorkflowNodeType.EmitEvent;

  constructor(
    private readonly params: Omit<DraftEmitEventNodeParams, 'id' | 'type'>,
    options?: WorkflowNodeConnectOptions
  ) {
    super(options);
  }

  override toSerializedNode(serialization: {
    workflowId: string;
    ownerId: string;
    ownerOrgId: string;
    createdAt: string;
    updatedAt: string;
  }) {
    const data: Record<string, JsonValue> = {
      eventName: this.params.eventName,
    };
    if (this.params.payloadBindings !== undefined) {
      data['payloadBindings'] = {
        ...this.params.payloadBindings,
      };
    }
    return this.baseSerializedNode({
      ...serialization,
      data,
      inputShape: this.params.inputShape ?? null,
      defaultOutput: this.params.defaultOutput ?? null,
    });
  }
}

export class MapperNode extends WorkflowGraphNode {
  readonly nodeType = WorkflowNodeType.Mapper;

  constructor(
    private readonly params: Omit<DraftMapperNodeParams, 'id' | 'type'>,
    options?: WorkflowNodeConnectOptions
  ) {
    super(options);
  }

  override toSerializedNode(serialization: {
    workflowId: string;
    ownerId: string;
    ownerOrgId: string;
    createdAt: string;
    updatedAt: string;
  }) {
    const data: Record<string, JsonValue> = {};
    if (this.params.name !== undefined) {
      data['name'] = this.params.name;
    }
    if (this.params.fieldBindings !== undefined) {
      data['fieldBindings'] = { ...this.params.fieldBindings };
    }
    // Pin the authored order now, while the bindings are still a JS object
    // that holds it — once stored as JSONB it is gone.
    const fieldOrder =
      this.params.fieldOrder ??
      (this.params.fieldBindings !== undefined
        ? Object.keys(this.params.fieldBindings)
        : undefined);
    if (fieldOrder !== undefined) {
      data['fieldOrder'] = [...fieldOrder];
    }
    if (this.params.output !== undefined) {
      data['output'] = this.params.output;
    }
    if (this.params.rowKey !== undefined) {
      data['rowKey'] = this.params.rowKey;
    }
    return this.baseSerializedNode({
      ...serialization,
      data,
      inputShape: this.params.inputShape ?? null,
      defaultOutput: this.params.defaultOutput ?? null,
    });
  }
}

/** Serializes a bucket flush policy into plain JSON (no casts). */
function serializeBucketPolicy(
  policy: DraftBucketNodeParams['policy']
): Record<string, JsonValue> {
  return {
    combinator: policy.combinator,
    triggers: policy.triggers.map(
      (trigger): Record<string, JsonValue> =>
        trigger.kind === 'count'
          ? { kind: 'count', threshold: trigger.threshold }
          : { kind: 'time', cron: trigger.cron, timezone: trigger.timezone }
    ),
  };
}

export class BucketNode extends WorkflowGraphNode {
  readonly nodeType = WorkflowNodeType.Bucket;

  constructor(
    private readonly params: Omit<DraftBucketNodeParams, 'id' | 'type'>,
    options?: WorkflowNodeConnectOptions
  ) {
    super(options);
  }

  override toSerializedNode(serialization: {
    workflowId: string;
    ownerId: string;
    ownerOrgId: string;
    createdAt: string;
    updatedAt: string;
  }) {
    const data: Record<string, JsonValue> = {
      policy: serializeBucketPolicy(this.params.policy),
    };
    if (this.params.name !== undefined) {
      data['name'] = this.params.name;
    }
    if (this.params.accumulate !== undefined) {
      data['accumulate'] = this.params.accumulate;
    }
    if (this.params.reducers !== undefined) {
      data['reducers'] = this.params.reducers.map(
        (reducer): Record<string, JsonValue> => ({
          op: reducer.op,
          path: reducer.path,
          as: reducer.as,
        })
      );
    }
    return this.baseSerializedNode({
      ...serialization,
      data: { ...(this.params.data ?? {}), ...data },
      inputShape: this.params.inputShape ?? null,
      defaultOutput: (this.params.defaultOutput ?? null) as JsonValue | null,
    });
  }
}

/**
 * A group-by node in a built graph.
 *
 * `keyPath` is required here while the persisted read schema leaves it loose:
 * a graph being constructed in code can be made correct by construction, and a
 * group-by with no key produces one group holding everything — which looks like
 * it worked. The loose read schema is for data already on disk, which has to be
 * readable whatever state it is in.
 */
export class GroupByNode extends WorkflowGraphNode {
  readonly nodeType = WorkflowNodeType.GroupBy;

  constructor(
    private readonly params: Omit<DraftGroupByNodeParams, 'id' | 'type'>,
    options?: WorkflowNodeConnectOptions
  ) {
    super(options);
  }

  override toSerializedNode(serialization: {
    workflowId: string;
    ownerId: string;
    ownerOrgId: string;
    createdAt: string;
    updatedAt: string;
  }) {
    const data: Record<string, JsonValue> = { keyPath: this.params.keyPath };
    if (this.params.name !== undefined) {
      data['name'] = this.params.name;
    }
    if (this.params.itemsPath !== undefined) {
      data['itemsPath'] = this.params.itemsPath;
    }
    if (this.params.sourceRunNodeId !== undefined) {
      data['sourceRunNodeId'] = this.params.sourceRunNodeId;
    }
    return this.baseSerializedNode({
      ...serialization,
      data: { ...(this.params.data ?? {}), ...data },
      inputShape: this.params.inputShape ?? null,
      defaultOutput: (this.params.defaultOutput ?? null) as JsonValue | null,
    });
  }
}

export type ForEachChildNode =
  | AgentNode
  | AdHocAgentNode
  | CompleteToolCallNode;

export class ForEachNode extends WorkflowGraphNode {
  readonly nodeType = WorkflowNodeType.ForEach;
  readonly child?: ForEachChildNode;

  constructor(
    private readonly params: Omit<
      DraftForEachNodeParams,
      'id' | 'type' | 'node'
    >,
    options?: WorkflowNodeConnectOptions & { readonly child?: ForEachChildNode }
  ) {
    super(options);
    this.child = options?.child;
  }

  override collectNodes(): readonly WorkflowGraphNode[] {
    if (this.child === undefined) {
      return [this];
    }
    return [this, this.child];
  }

  override toSerializedNode(serialization: {
    workflowId: string;
    ownerId: string;
    ownerOrgId: string;
    createdAt: string;
    updatedAt: string;
  }) {
    return this.baseSerializedNode({
      ...serialization,
      data: {
        ...(this.params.data ?? {}),
        ...(this.params.itemsPath !== undefined && {
          itemsPath: this.params.itemsPath,
        }),
        ...(this.params.sourceRunNodeId !== undefined && {
          sourceRunNodeId: this.params.sourceRunNodeId,
        }),
        ...(this.child !== undefined && { childNodeId: this.child.id }),
      },
      inputShape: this.params.inputShape ?? null,
      defaultOutput: (this.params.defaultOutput ?? null) as JsonValue | null,
    });
  }
}

/**
 * Debounce node: lets a key through once per window and suppresses it for the
 * rest of that window. Only `keyPath` is required; window, timezone and week
 * start fall back to the SDK defaults at parse time, so a node authored with
 * just a key is valid.
 */
export class DebounceNode extends WorkflowGraphNode {
  readonly nodeType = WorkflowNodeType.Debounce;

  constructor(
    private readonly params: Omit<DraftDebounceNodeParams, 'id' | 'type'>,
    options?: WorkflowNodeConnectOptions
  ) {
    super(options);
  }

  override toSerializedNode(serialization: {
    workflowId: string;
    ownerId: string;
    ownerOrgId: string;
    createdAt: string;
    updatedAt: string;
  }) {
    const data: Record<string, JsonValue> = { keyPath: this.params.keyPath };
    if (this.params.window !== undefined) {
      data['window'] = this.params.window;
    }
    if (this.params.timezone !== undefined) {
      data['timezone'] = this.params.timezone;
    }
    if (this.params.weekStartsOn !== undefined) {
      data['weekStartsOn'] = this.params.weekStartsOn;
    }
    return this.baseSerializedNode({
      ...serialization,
      data,
      inputShape: this.params.inputShape ?? null,
      defaultOutput: this.params.defaultOutput ?? null,
    });
  }
}
